import { ProbateDetails } from "../models/ProbateDetails";
import { callDelete, callGet, callPost, } from "./index";
import { ProbateField, } from "../models/ProbateField";
import { AtticusForm } from "../models/AtticusForm";
import { CognitoAuth } from "./CognitoAuth";
import { LeadAPI } from "./LeadAPI";
import { knowledgeEngineDataPrefix } from "LeadProbateContextWrapper";
import { isLead } from "../constants"

export class ProbateAPI {
  async getProbateDetails(
    estateId: number,
    queryString?: string
  ): Promise<ProbateDetails | undefined> {
    try {
      let path = `/estates/{estateId}/determine-probate-type`;
      if (queryString) path += `?${queryString}`;
      const response = await callGet(path, true);
      return ProbateDetails.fromJSON(response?.data);
    } catch (error) {
      !isLead && CognitoAuth.handleError(error, "getProbateDetails error");
    }
  }

  async getProbateDetailsForLead(
    countryId: number,
    stateId: number,
    givenAnswers: ProbateField[],
  ): Promise<ProbateDetails | undefined> {
    try {
      // we need to base64 encode these values because they
      // sometimes include non-ISO-8859-1 characters such as curvey single quotes
      const answersHeaderValue = JSON.stringify(givenAnswers || [])
      const path = `/leads/determine-probate-type`;
      const response = await callPost(path,
        {
          given_answers: answersHeaderValue,
          country_id: countryId,
          state_id: stateId,
        },
        true);
      return ProbateDetails.fromJSON(response?.data);
    } catch (error) {
      if ((error as any)?.response?.status === 500) {
        console.error("got 500 from api")
        return undefined
      }
      throw error
    }
  }

  /*
          Route::get('/forms/probate-type', 'ProbateTypeController@determineProbateType');
          Route::get('/knowledge-engine/fields', 'KnowledgeEngineController@getAllFields');
          Route::get('/knowledge-engine/fields-and-answers-for-user', 'KnowledgeEngineController@getAllFieldsAndAnswersForUser');
          Route::get('/knowledge-engine/categories', 'KnowledgeEngineController@getAllCategories');
          Route::get('/knowledge-engine/field-answer/{field_id}', 'KnowledgeEngineController@getFieldAnswer');
          Route::post('/knowledge-engine/field-answer/{field_id}', 'KnowledgeEngineController@storeFieldAnswer');
          Route::delete('/knowledge-engine/field-answer/{field_id}', 'KnowledgeEngineController@deleteAnswer');
  */

  async getFieldAnswerForUser(fieldId: string): Promise<string | boolean | number | undefined> {
    try {
      const path = `/knowledge-engine/field-answer/${fieldId}`;
      const response = await callGet(
        path,
        true
      );
      return response?.data?.answer_value
    } catch (error) {
      !isLead && CognitoAuth.handleError(error, "getFieldAnswerForUser error");
    }

    return undefined
  }

  async getFieldAnswerForOtherUser(otherUserId: number, fieldId: string): Promise<string | boolean | number | undefined> {
    try {
      const path = `/knowledge-engine/other-user-field-answer/${otherUserId}/${fieldId}`;
      const response = await callGet(
        path,
        true
      );
      return response?.data?.answer_value
    } catch (error) {
      !isLead && CognitoAuth.handleError(error, "getFieldAnswerForOtherUser error");
    }

    return undefined
  }

  async saveFieldToKnowledgeEngine(
    field: ProbateField,
    rethrowError = false
  ): Promise<any | undefined> {
    try {
      if (field?.id) {
        const path = `/knowledge-engine/field-answer/${field.id}`;
        return await callPost(
          path,
          {
            answer_value: field.answerValue,
          },
          rethrowError
        );
      } else {
        throw new Error("field id unknown");
      }
    } catch (error) {
      !isLead && CognitoAuth.handleError(error, "saveFieldToKnowledgeEngine error");
    }
  }

  async saveFieldToKnowledgeEngineForOtherUser(
    otherUserId: number,
    field: ProbateField,
    rethrowError = false
  ): Promise<any | undefined> {
    try {
      if (field?.id) {
        const path = `/knowledge-engine/other-user-field-answer/${otherUserId}/${field.id}`;
        return await callPost(
          path,
          {
            answer_value: field.answerValue,
          },
          rethrowError
        );
      } else {
        throw new Error("field id unknown");
      }
    } catch (error) {
      !isLead && CognitoAuth.handleError(error, "saveFieldToKnowledgeEngineForOtherUser error");
    }
  }


  async saveFieldToKnowledgeEngineForLead(
    leadKey: string,
    field: ProbateField,
  ): Promise<any | undefined> {
    try {
      if (field?.id) {
        await LeadAPI.upsertLeadData(leadKey, { [knowledgeEngineDataPrefix + field.id]: field.answer })
      } else {
        throw new Error("field id unknown");
      }
    } catch (error) {
    }
  }

  /**
   * will delete knowledge engine answers for this estate
   * @param field
   * @param rethrowError
   */
  async deleteFieldsInKnowledgeEngine(
    field: ProbateField | string,
    rethrowError = false
  ): Promise<any | undefined> {
    try {
      if (field instanceof ProbateField) {
        // NOTE: this is currently not supported by the laravel api
        if (field?.id) {
          const path = `/knowledge-engine/field-answer/${field.id}`;
          return await callPost(
            path,
            {
              answer_value: field.answerValue,
            },
            rethrowError
          );
        } else {
          throw new Error("field id unknown");
        }
      } else if (field === "all") {
        const path = `/knowledge-engine/field-answer/all`;
        return await callDelete(path, rethrowError);
      } else {
        throw new Error("delete operation unsupported");
      }
    } catch (error) {
      !isLead && CognitoAuth.handleError(error, "deleteFieldInKnowledgeEngine error");
    }
  }


  async deleteFieldsInKnowledgeEngineForLead(
    leadKey: string,
    field: ProbateField,
  ): Promise<any | undefined> {
    try {
      if (field?.id) {
        await LeadAPI.upsertLeadData(leadKey, { [knowledgeEngineDataPrefix + field.id]: null })
      } else {
        throw new Error("field id unknown");
      }
    } catch (error) {
    }
  }

  async getProbatePacket(): Promise<AtticusForm[] | undefined> {
    try {
      const path = `/estates/first/probate-packet`;
      const packetResponse = await callGet(path, true);
      return packetResponse?.data?.map((json: any) =>
        AtticusForm.fromJSON(json)
      );
    } catch (error) {
      // NOTE: intentionally not signing out when 401 is returned
      return undefined;
    }
  }
}
